/**
 * Cohort Manager Module
 * Manages state, UI rendering, and persistence for the Data Aggregator feature
 */

const CohortManager = {
    cohorts: [],
    startDate: '',
    endDate: '',
    maxCohorts: 3,
    cookieName: 'absent_aggregator',
    cookieExpiry: 15552000, // 6 months in seconds
    filterOptions: {},

    /**
     * Initialize the cohort manager
     */
    init() {
        this.loadFilterOptions();
        this.loadFromCookie();
        this.bindEvents();
        this.render();
    },

    /**
     * Load available filter options from the server
     */
    loadFilterOptions() {
        // Filter options will be passed via wp_localize_script
        if (typeof cohortFilterOptions !== 'undefined') {
            this.filterOptions = cohortFilterOptions;
        }
    },

    /**
     * Bind event listeners
     */
    bindEvents() {
        // Add cohort button
        document.getElementById('add-cohort-btn')?.addEventListener('click', () => {
            this.addCohort();
        });

        // Clear settings button
        document.getElementById('clear-settings-btn')?.addEventListener('click', () => {
            const modal = new bootstrap.Modal(document.getElementById('clearSettingsModal'));
            modal.show();
        });

        // Confirm clear button in modal
        document.getElementById('confirm-clear-btn')?.addEventListener('click', () => {
            this.clearCookie();
            const modal = bootstrap.Modal.getInstance(document.getElementById('clearSettingsModal'));
            modal.hide();
        });

        // Apply button
        document.getElementById('apply-cohorts-btn')?.addEventListener('click', () => {
            this.applyCohorts();
        });

        // Date input change listeners - save to cookie when dates change
        this.bindDateInputEvents();
    },

    /**
     * Bind change events to date inputs for UI updates
     */
    bindDateInputEvents() {
        const startDateInput = document.getElementById('aggregator_start_date');
        const endDateInput = document.getElementById('aggregator_end_date');

        if (startDateInput) {
            startDateInput.addEventListener('change', () => {
                this.startDate = startDateInput.value;
                this.updateDateClearButton(startDateInput, startDateInput.value);
                // Toggle bold label
                const label = startDateInput.closest('.col-md-6')?.querySelector('.form-label');
                if (label) {
                    label.classList.toggle('filter-label-active', !!startDateInput.value);
                }
            });
        }

        if (endDateInput) {
            endDateInput.addEventListener('change', () => {
                this.endDate = endDateInput.value;
                this.updateDateClearButton(endDateInput, endDateInput.value);
                // Toggle bold label
                const label = endDateInput.closest('.col-md-6')?.querySelector('.form-label');
                if (label) {
                    label.classList.toggle('filter-label-active', !!endDateInput.value);
                }
            });
        }
    },

    /**
     * Add a new cohort
     */
    addCohort() {
        if (this.cohorts.length >= this.maxCohorts) {
            this.showToast('Maximum 3 cohorts allowed', 'warning');
            return;
        }

        const cohort = {
            id: Date.now(),
            name: `Cohort ${this.cohorts.length + 1}`,
            color: this.generateColor(),
            filter_type: 'attributes',
            filters: {
                counties: [],
                regions: [],
                in_game_network: 0
            },
            district_irns: [],
            district_names: {} // Store IRN -> Name mapping
        };

        this.cohorts.push(cohort);
        this.render();
    },

    /**
     * Remove a cohort by ID
     */
    removeCohort(id) {
        this.cohorts = this.cohorts.filter(c => c.id !== id);
        this.render();
    },

    /**
     * Update a cohort
     */
    updateCohort(id, updates) {
        const cohort = this.cohorts.find(c => c.id === id);
        if (cohort) {
            Object.assign(cohort, updates);
            this.updateApplyButton();
        }
    },

    /**
     * Generate a color for a new cohort
     */
    generateColor() {
        const colors = ['#0066cc', '#cc0066', '#00cc66', '#cc6600', '#6600cc', '#00cccc'];
        const usedColors = this.cohorts.map(c => c.color);
        return colors.find(c => !usedColors.includes(c)) || colors[0];
    },

    /**
     * Save cohorts and date filters to cookie
     */
    saveToCookie() {
        try {
            // Get current date values from inputs
            const startDateInput = document.getElementById('aggregator_start_date');
            const endDateInput = document.getElementById('aggregator_end_date');
            this.startDate = startDateInput ? startDateInput.value : '';
            this.endDate = endDateInput ? endDateInput.value : '';

            // Save cohorts and dates as a single object
            const data = JSON.stringify({
                cohorts: this.cohorts,
                start_date: this.startDate,
                end_date: this.endDate
            });
            document.cookie = `${this.cookieName}=${encodeURIComponent(data)}; max-age=${this.cookieExpiry}; path=/; SameSite=Strict`;
        } catch (e) {
            //console.error('Failed to save cohorts to cookie', e);
            this.showToast('Failed to save settings', 'danger');
        }
    },

    /**
     * Load cohorts and date filters from cookie
     */
    loadFromCookie() {
        const match = document.cookie.match(new RegExp(this.cookieName + '=([^;]+)'));
        if (match) {
            try {
                const parsed = JSON.parse(decodeURIComponent(match[1]));

                // Handle both old format (array of cohorts) and new format (object with cohorts and dates)
                if (Array.isArray(parsed)) {
                    // Old format: just an array of cohorts
                    this.cohorts = parsed;
                    this.startDate = '';
                    this.endDate = '';
                } else {
                    // New format: object with cohorts and dates
                    this.cohorts = parsed.cohorts || [];
                    this.startDate = parsed.start_date || '';
                    this.endDate = parsed.end_date || '';
                }

                // Populate date inputs if they exist and have no URL values
                this.populateDateInputs();
            } catch (e) {
                //console.error('Failed to parse cohort cookie', e);
                this.cohorts = [];
                this.startDate = '';
                this.endDate = '';
            }
        }
    },

    /**
     * Populate date input fields from stored values
     * Only populates if the inputs are empty (no URL param values)
     */
    populateDateInputs() {
        const startDateInput = document.getElementById('aggregator_start_date');
        const endDateInput = document.getElementById('aggregator_end_date');

        // Only populate if input is empty (no URL param value)
        if (startDateInput && !startDateInput.value && this.startDate) {
            startDateInput.value = this.startDate;
            // Toggle bold label
            const startLabel = startDateInput.closest('.col-md-6')?.querySelector('.form-label');
            if (startLabel) {
                startLabel.classList.add('filter-label-active');
            }
            // Add clear button if not present
            this.updateDateClearButton(startDateInput, this.startDate);
        }

        if (endDateInput && !endDateInput.value && this.endDate) {
            endDateInput.value = this.endDate;
            // Toggle bold label
            const endLabel = endDateInput.closest('.col-md-6')?.querySelector('.form-label');
            if (endLabel) {
                endLabel.classList.add('filter-label-active');
            }
            // Add clear button if not present
            this.updateDateClearButton(endDateInput, this.endDate);
        }
    },

    /**
     * Update date field clear button visibility
     */
    updateDateClearButton(input, value) {
        const label = input.closest('.col-md-6')?.querySelector('.form-label');
        if (!label) return;

        let clearBtn = label.querySelector('.filter-clear-btn');

        if (value) {
            // Add clear button if it doesn't exist
            if (!clearBtn) {
                clearBtn = document.createElement('button');
                clearBtn.type = 'button';
                clearBtn.className = 'btn btn-link p-0 filter-clear-btn filter-clear-btn--inline';
                clearBtn.dataset.filter = input.id;
                clearBtn.innerHTML = `<i class="bi bi-x-circle text-danger" aria-hidden="true"></i><span class="visually-hidden">Clear ${input.id.replace('_', ' ')} filter</span>`;
                label.appendChild(clearBtn);

                // Bind click event
                clearBtn.addEventListener('click', () => {
                    // Clear via Flatpickr if initialized, otherwise clear directly
                    if (input._flatpickr) {
                        input._flatpickr.clear();
                    } else {
                        input.value = '';
                    }
                    clearBtn.remove();
                    label.classList.remove('filter-label-active');
                });
            }
        } else {
            // Remove clear button if it exists
            if (clearBtn) {
                clearBtn.remove();
            }
        }
    },

    /**
     * Clear cookie and reset
     */
    clearCookie() {
        document.cookie = `${this.cookieName}=; max-age=0; path=/`;
        this.cohorts = [];
        this.startDate = '';
        this.endDate = '';

        // Clear date input fields
        const startDateInput = document.getElementById('aggregator_start_date');
        const endDateInput = document.getElementById('aggregator_end_date');

        if (startDateInput) {
            startDateInput.value = '';
            // Clear via Flatpickr if initialized
            if (startDateInput._flatpickr) {
                startDateInput._flatpickr.clear();
            }
            // Remove bold label and clear button
            const startLabel = startDateInput.closest('.col-md-6')?.querySelector('.form-label');
            if (startLabel) {
                startLabel.classList.remove('filter-label-active');
                const startClearBtn = startLabel.querySelector('.filter-clear-btn');
                if (startClearBtn) startClearBtn.remove();
            }
        }

        if (endDateInput) {
            endDateInput.value = '';
            // Clear via Flatpickr if initialized
            if (endDateInput._flatpickr) {
                endDateInput._flatpickr.clear();
            }
            // Remove bold label and clear button
            const endLabel = endDateInput.closest('.col-md-6')?.querySelector('.form-label');
            if (endLabel) {
                endLabel.classList.remove('filter-label-active');
                const endClearBtn = endLabel.querySelector('.filter-clear-btn');
                if (endClearBtn) endClearBtn.remove();
            }
        }

        this.render();
        this.showToast('Settings cleared', 'success');
    },

    /**
     * Validate all cohorts
     */
    validate() {
        if (this.cohorts.length === 0) {
            return { valid: false, message: 'Add at least one cohort' };
        }

        for (const cohort of this.cohorts) {
            if (!cohort.name.trim()) {
                return { valid: false, message: 'All cohorts must have a name' };
            }

            if (cohort.filter_type === 'attributes') {
                const hasFilters = cohort.filters.counties.length > 0 ||
                                  cohort.filters.regions.length > 0 ||
                                  cohort.filters.in_game_network === 1;
                if (!hasFilters) {
                    return { valid: false, message: `Cohort "${cohort.name}" has no filters selected` };
                }
            } else if (cohort.filter_type === 'districts') {
                if (cohort.district_irns.length === 0) {
                    return { valid: false, message: `Cohort "${cohort.name}" has no districts selected` };
                }
            }
        }

        return { valid: true };
    },

    /**
     * Apply cohorts and submit form
     */
    applyCohorts() {
        const validation = this.validate();
        if (!validation.valid) {
            this.showToast(validation.message, 'danger');
            return;
        }

        // Save cohort settings to cookie on submit
        this.saveToCookie();

        // Build URL with cohort parameters
        const url = new URL(window.location.href);

        // Clear Basic Search parameters to avoid pollution
        const basicSearchParams = [
            'counties[]', 'regions[]',
            'in_game_network', 'sort_by', 'sort_order',
            'page_num', 'per_page'
        ];
        basicSearchParams.forEach(param => url.searchParams.delete(param));

        // Set aggregator parameters
        url.searchParams.set('cohorts', JSON.stringify(this.cohorts));
        url.searchParams.set('start_date', document.getElementById('aggregator_start_date')?.value || '');
        url.searchParams.set('end_date', document.getElementById('aggregator_end_date')?.value || '');

        window.location.href = url.toString();
    },

    /**
     * Update the apply button state
     */
    updateApplyButton() {
        const btn = document.getElementById('apply-cohorts-btn');
        if (!btn) return;

        const validation = this.validate();
        btn.disabled = !validation.valid;
    },

    /**
     * Render all cohorts
     */
    render() {
        const container = document.getElementById('cohorts-container');
        const emptyState = document.getElementById('empty-state');
        const addBtn = document.getElementById('add-cohort-btn');

        if (!container) return;

        // Clear container
        container.innerHTML = '';

        // Show/hide empty state
        if (this.cohorts.length === 0) {
            container.appendChild(emptyState);
        } else {
            // Render each cohort
            this.cohorts.forEach(cohort => {
                const card = this.renderCohortCard(cohort);
                container.appendChild(card);
            });
        }

        // Update add button state
        if (addBtn) {
            addBtn.disabled = this.cohorts.length >= this.maxCohorts;
        }

        // Update apply button
        this.updateApplyButton();

        // Update active indicators
        this.updateActiveIndicators();
    },

    /**
     * Update active indicators for all cohorts
     */
    updateActiveIndicators() {
        document.querySelectorAll('.cohort-card').forEach(card => {
            const cohortId = parseInt(card.dataset.cohortId);
            const cohort = this.cohorts.find(c => c.id === cohortId);

            if (cohort) {
                // Update filter labels with bold class
                ['counties', 'regions', 'in_game_network'].forEach(filterKey => {
                    const label = card.querySelector(`[data-filter-label="${filterKey}"]`);
                    if (label) {
                        const isActive = filterKey === 'in_game_network'
                            ? cohort.filters.in_game_network === 1
                            : cohort.filters[filterKey].length > 0;
                        label.classList.toggle('filter-label-active', isActive);
                    }
                });

                // Manage clear button for In The Game checkbox (next to label)
                const inGameLabel = card.querySelector('[data-filter-label="in_game_network"]');
                if (inGameLabel) {
                    let inGameClearBtn = inGameLabel.querySelector('.filter-clear-btn');
                    if (cohort.filters.in_game_network === 1) {
                        if (!inGameClearBtn) {
                            inGameClearBtn = document.createElement('button');
                            inGameClearBtn.type = 'button';
                            inGameClearBtn.className = 'btn btn-link p-0 filter-clear-btn filter-clear-btn--inline';
                            inGameClearBtn.dataset.filter = 'in_game_network';
                            inGameClearBtn.innerHTML = '<i class="bi bi-x-circle text-danger" aria-hidden="true"></i><span class="visually-hidden">Clear In The Game Network filter</span>';
                            inGameLabel.appendChild(inGameClearBtn);

                            inGameClearBtn.addEventListener('click', () => {
                                const cohortObj = this.cohorts.find(c => c.id === cohortId);
                                if (cohortObj) {
                                    cohortObj.filters.in_game_network = 0;
                                    card.querySelector('.in-game-checkbox').checked = false;
                                    this.updateApplyButton();
                                    this.render();
                                }
                            });
                        }
                    } else {
                        if (inGameClearBtn) {
                            inGameClearBtn.remove();
                        }
                    }
                }
            }
        });
    },

    /**
     * Render a single cohort card
     */
    renderCohortCard(cohort) {
        const template = document.getElementById('cohort-card-template');
        const card = template.content.cloneNode(true).querySelector('.cohort-card');

        // Set cohort ID
        card.dataset.cohortId = cohort.id;

        // Set values
        const nameInput = card.querySelector('.cohort-name-input');
        const colorPicker = card.querySelector('.cohort-color-picker');
        nameInput.value = cohort.name;
        colorPicker.value = cohort.color;

        // Set filter type via tabs
        if (cohort.filter_type === 'districts') {
            const tabs = card.querySelectorAll('.filter-type-tab');
            tabs[0].classList.remove('active');
            tabs[0].setAttribute('aria-selected', 'false');
            tabs[1].classList.add('active');
            tabs[1].setAttribute('aria-selected', 'true');
            card.querySelector('.attributes-fieldset').style.display = 'none';
            card.querySelector('.districts-fieldset').style.display = 'block';
        }

        // Bind events
        this.bindCohortEvents(card, cohort);

        // Render filter dropdowns
        this.renderAttributeFilters(card, cohort);

        // Render selected districts
        this.renderSelectedDistricts(card, cohort);

        return card;
    },

    /**
     * Bind events for a cohort card
     */
    bindCohortEvents(card, cohort) {
        const cohortId = cohort.id;

        // Name input
        card.querySelector('.cohort-name-input').addEventListener('input', (e) => {
            this.updateCohort(cohortId, { name: e.target.value });
        });

        // Color picker
        card.querySelector('.cohort-color-picker').addEventListener('change', (e) => {
            this.updateCohort(cohortId, { color: e.target.value });
        });

        // Remove button
        card.querySelector('.remove-cohort-btn').addEventListener('click', () => {
            this.removeCohort(cohortId);
        });

        // Filter type toggle via tabs
        card.querySelectorAll('.filter-type-tab').forEach(tab => {
            tab.addEventListener('click', (e) => {
                const filterType = e.currentTarget.dataset.filterType;
                this.updateCohort(cohortId, { filter_type: filterType });

                // Update active tab state
                card.querySelectorAll('.filter-type-tab').forEach(t => {
                    t.classList.remove('active');
                    t.setAttribute('aria-selected', 'false');
                });
                e.currentTarget.classList.add('active');
                e.currentTarget.setAttribute('aria-selected', 'true');

                // Toggle fieldsets
                const attributesFieldset = card.querySelector('.attributes-fieldset');
                const districtsFieldset = card.querySelector('.districts-fieldset');

                if (filterType === 'attributes') {
                    attributesFieldset.style.display = 'block';
                    districtsFieldset.style.display = 'none';
                } else {
                    attributesFieldset.style.display = 'none';
                    districtsFieldset.style.display = 'block';
                }

                this.updateApplyButton();
            });
        });

        // In The Game checkbox
        card.querySelector('.in-game-checkbox').addEventListener('change', (e) => {
            const cohortObj = this.cohorts.find(c => c.id === cohortId);
            cohortObj.filters.in_game_network = e.target.checked ? 1 : 0;
            this.updateApplyButton();
            this.updateActiveIndicators();
        });

        // District search
        const searchInput = card.querySelector('.district-search-input');
        DistrictSearch.bindToInput(searchInput, cohortId);
    },

    /**
     * Render attribute filter dropdowns
     */
    renderAttributeFilters(card, cohort) {
        const countyContainer = card.querySelector('.county-dropdown');
        const regionContainer = card.querySelector('.region-dropdown');

        this.renderCheckboxDropdown(countyContainer, 'counties', cohort, this.filterOptions.counties || []);
        this.renderCheckboxDropdown(regionContainer, 'regions', cohort, this.filterOptions.regions || []);
    },

    /**
     * Render a checkbox dropdown (like Basic Search)
     */
    renderCheckboxDropdown(container, filterKey, cohort, options) {
        const uniqueId = `${filterKey}-${cohort.id}`;
        const selected = cohort.filters[filterKey];
        const hasActive = selected.length > 0;

        const html = `
            <div class="dropdown" data-filter-group="${filterKey}">
                <button class="form-control dropdown-toggle d-flex gap-2 w-100 text-start"
                        type="button"
                        id="dropdown-${uniqueId}"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside">
                    <span class="me-auto">${this.capitalizeFirst(filterKey)}</span>
                    ${hasActive ? `<span class="options-count">${selected.length}</span>` : ''}
                </button>
                <ul class="dropdown-menu p-2" style="">
                    ${options.map(option => `
                        <li>
                            <div class="form-check">
                                <input class="form-check-input cohort-filter-checkbox"
                                       type="checkbox"
                                       value="${option}"
                                       data-filter-key="${filterKey}"
                                       data-cohort-id="${cohort.id}"
                                       id="check-${uniqueId}-${option.replace(/\s+/g, '-')}"
                                       ${selected.includes(option) ? 'checked' : ''}>
                                <label class="form-check-label" for="check-${uniqueId}-${option.replace(/\s+/g, '-')}">
                                    ${option}
                                </label>
                            </div>
                        </li>
                    `).join('')}
                </ul>
            </div>
        `;

        container.innerHTML = html;

        // Manage clear button on the label (sibling above the container)
        const card = container.closest('.cohort-card');
        const label = card?.querySelector(`[data-filter-label="${filterKey}"]`);
        if (label) {
            const existingBtn = label.querySelector('.filter-clear-btn');
            if (hasActive) {
                if (!existingBtn) {
                    const clearBtn = document.createElement('button');
                    clearBtn.type = 'button';
                    clearBtn.className = 'btn btn-link p-0 filter-clear-btn filter-clear-btn--inline';
                    clearBtn.dataset.filterKey = filterKey;
                    clearBtn.dataset.cohortId = cohort.id;
                    clearBtn.innerHTML = `<i class="bi bi-x-circle text-danger" aria-hidden="true"></i><span class="visually-hidden">Clear ${filterKey} filter</span>`;
                    label.appendChild(clearBtn);
                }
            } else {
                if (existingBtn) {
                    existingBtn.remove();
                }
            }
        }

        // Bind checkbox change events
        container.querySelectorAll('.cohort-filter-checkbox').forEach(checkbox => {
            checkbox.addEventListener('change', (e) => {
                const cohortObj = this.cohorts.find(c => c.id === cohort.id);
                const value = e.target.value;

                if (e.target.checked) {
                    if (!cohortObj.filters[filterKey].includes(value)) {
                        cohortObj.filters[filterKey].push(value);
                    }
                } else {
                    cohortObj.filters[filterKey] = cohortObj.filters[filterKey].filter(v => v !== value);
                }

                this.updateApplyButton();

                // Update count badge and clear button in place (no full re-render)
                this.updateDropdownIndicators(container, filterKey, cohortObj);
            });
        });

        // Bind clear button (now on the label, not inside container)
        const clearBtn = label?.querySelector('.filter-clear-btn');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                const cohortObj = this.cohorts.find(c => c.id === cohort.id);
                cohortObj.filters[filterKey] = [];
                this.updateApplyButton();
                this.render();
            });
        }
    },

    /**
     * Update dropdown count badge and clear button without full re-render
     */
    updateDropdownIndicators(container, filterKey, cohort) {
        const selected = cohort.filters[filterKey];
        const hasActive = selected.length > 0;

        // Update the count badge on the dropdown button
        const dropdownBtn = container.querySelector('.dropdown-toggle');
        if (dropdownBtn) {
            let countBadge = dropdownBtn.querySelector('.options-count');
            if (hasActive) {
                if (!countBadge) {
                    countBadge = document.createElement('span');
                    countBadge.className = 'options-count';
                    dropdownBtn.appendChild(countBadge);
                }
                countBadge.textContent = selected.length;
                countBadge.setAttribute('aria-label', selected.length + ' selected');
            } else if (countBadge) {
                countBadge.remove();
            }
        }

        // Update the label bold state and clear button
        const card = container.closest('.cohort-card');
        const label = card?.querySelector(`[data-filter-label="${filterKey}"]`);
        if (label) {
            label.classList.toggle('filter-label-active', hasActive);

            let clearBtn = label.querySelector('.filter-clear-btn');
            if (hasActive) {
                if (!clearBtn) {
                    clearBtn = document.createElement('button');
                    clearBtn.type = 'button';
                    clearBtn.className = 'btn btn-link p-0 filter-clear-btn filter-clear-btn--inline';
                    clearBtn.dataset.filterKey = filterKey;
                    clearBtn.dataset.cohortId = cohort.id;
                    clearBtn.innerHTML = `<i class="bi bi-x-circle text-danger" aria-hidden="true"></i><span class="visually-hidden">Clear ${filterKey} filter</span>`;
                    label.appendChild(clearBtn);

                    clearBtn.addEventListener('click', () => {
                        cohort.filters[filterKey] = [];
                        this.updateApplyButton();
                        this.render();
                    });
                }
            } else if (clearBtn) {
                clearBtn.remove();
            }
        }
    },

    /**
     * Capitalize first letter
     */
    capitalizeFirst(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    },

    /**
     * Render selected districts as badges
     */
    renderSelectedDistricts(card, cohort) {
        const container = card.querySelector('.selected-districts');
        container.innerHTML = '';

        if (cohort.district_irns.length === 0) {
            container.innerHTML = '<p class="text-muted small">No districts selected</p>';
            return;
        }

        cohort.district_irns.forEach(irn => {
            const badge = document.createElement('span');
            badge.className = 'district-badge';
            badge.innerHTML = `
                ${cohort.district_names[irn] || irn}
                <i class="bi bi-x-circle text-danger ms-1 remove-district" data-irn="${irn}"></i>
            `;

            badge.querySelector('.remove-district').addEventListener('click', () => {
                this.removeDistrict(cohort.id, irn);
            });

            container.appendChild(badge);
        });
    },

    /**
     * Remove a district from a cohort
     */
    removeDistrict(cohortId, irn) {
        const cohort = this.cohorts.find(c => c.id === cohortId);
        if (cohort) {
            cohort.district_irns = cohort.district_irns.filter(i => i !== irn);
            delete cohort.district_names[irn];
            this.render();
        }
    },

    /**
     * Show a toast notification
     */
    showToast(message, type = 'info') {
        // Create a simple Bootstrap alert
        const alert = document.createElement('div');
        alert.className = `alert alert-${type} alert-dismissible fade show position-fixed top-0 end-0 m-3`;
        alert.style.zIndex = '9999';
        alert.innerHTML = `
            ${message}
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;
        document.body.appendChild(alert);

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
            alert.remove();
        }, 5000);
    }
};

/**
 * District Search Module
 * Handles district autocomplete functionality
 */
const DistrictSearch = {
    debounceTimer: null,
    currentInput: null,
    currentCohortId: null,

    /**
     * Bind search functionality to an input element
     */
    bindToInput(inputElement, cohortId) {
        inputElement.addEventListener('input', (e) => {
            this.currentInput = inputElement;
            this.currentCohortId = cohortId;

            clearTimeout(this.debounceTimer);
            const term = e.target.value.trim();

            if (term.length < 3) {
                this.hideResults(inputElement);
                return;
            }

            this.debounceTimer = setTimeout(() => {
                this.search(term, inputElement);
            }, 300);
        });

        // Close results when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.districts-fieldset')) {
                this.hideResults(inputElement);
            }
        });
    },

    /**
     * Search for districts
     */
    search(term, inputElement) {
        const resultsContainer = inputElement.closest('.position-relative').querySelector('.district-results');
        this.showLoading(resultsContainer);

        jQuery.post(cohortAjax.ajaxurl, {
            action: 'search_districts',
            nonce: cohortAjax.nonce,
            search: term
        })
        .done((response) => {
            if (response.success) {
                this.renderResults(resultsContainer, response.data);
            } else {
                this.showError(resultsContainer, response.data);
            }
        })
        .fail(() => {
            this.showError(resultsContainer, 'Unable to load districts. Please try again.');
        });
    },

    /**
     * Show loading state
     */
    showLoading(container) {
        container.style.display = 'block';
        container.innerHTML = '<div class="p-2 text-center"><div class="spinner-border spinner-border-sm"></div> Loading...</div>';
    },

    /**
     * Show error message
     */
    showError(container, message) {
        container.style.display = 'block';
        container.innerHTML = `<div class="p-2 text-danger">${message}</div>`;
    },

    /**
     * Hide results
     */
    hideResults(inputElement) {
        const resultsContainer = inputElement.closest('.position-relative').querySelector('.district-results');
        resultsContainer.style.display = 'none';
        resultsContainer.innerHTML = '';
    },

    /**
     * Render search results
     */
    renderResults(container, districts) {
        if (districts.length === 0) {
            container.innerHTML = '<div class="p-2 text-muted">No districts found</div>';
            return;
        }

        container.style.display = 'block';
        container.innerHTML = '';

        districts.forEach(district => {
            const item = document.createElement('div');
            item.className = 'district-item';
            item.innerHTML = `
                <strong>${district.DistrictName}</strong>
                <div class="small text-muted">${district.County} County</div>
            `;

            item.addEventListener('click', () => {
                this.addDistrict(district.DistrictIRN, district.DistrictName);
                this.hideResults(this.currentInput);
                this.currentInput.value = '';
            });

            container.appendChild(item);
        });
    },

    /**
     * Add a district to the current cohort
     */
    addDistrict(irn, name) {
        const cohort = CohortManager.cohorts.find(c => c.id === this.currentCohortId);

        if (!cohort) {
            return;
        }

        if (cohort.district_irns.includes(irn)) {
            CohortManager.showToast('District already added', 'warning');
            return;
        }

        cohort.district_irns.push(irn);
        cohort.district_names[irn] = name;
        CohortManager.render();
        CohortManager.showToast('District added', 'success');
    }
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Only initialize if we're on the aggregator page
    if (document.getElementById('cohort-builder-container')) {
        CohortManager.init();
    }
});
